import React, { FC, useState } from "react";
import Title from "antd/lib/typography/Title";
import { Avatar, Card, Divider, Empty, List } from "antd";
import { RightOutlined } from "@ant-design/icons";
import { useIntl } from "react-intl";
import { useRouter } from "next/router";
import { useGetPortalFacilityHierarchyQuery } from "../../src/generated/graphql";
import { extractTranslation } from "../../helpers/formatters";
import styles from "./facility-selection.module.scss";
import { AveliosSpinner } from "../loading/avelios-spinner";
import {
  buildImageDownloadUrl,
  highlightTitle,
  useCurrentUser,
} from "../../helpers/utils";
import { FacilitySearch } from "./facility-search";

export const FacilityList: FC = () => {
  const { formatMessage } = useIntl();

  const router = useRouter();
  const locale = router.locale;

  const { currentUser } = useCurrentUser();

  const { query } = router;
  const facilityId = query.facilityId as string | undefined;

  const [searchQuery, setSearchQuery] = useState<string>();

  const { data: facilitiesData, loading } = useGetPortalFacilityHierarchyQuery({
    variables: { depth: facilityId ? 1 : 0, rootPortalFacilityId: facilityId },
  });
  const facilities =
    facilitiesData?.getPortalFacilityHierarchy?.portalFacilities?.filter(
      (facility) => facility?.id !== facilityId
    );

  const filteredFacilities = facilities?.filter(
    (facility) =>
      !searchQuery ||
      facility?.name
        ?.toLocaleLowerCase()
        .includes(searchQuery.toLowerCase() || "") ||
      extractTranslation(facility?.shortDescription, locale)
        ?.toLocaleLowerCase()
        .includes(searchQuery.toLowerCase() || "")
  );

  const rootFacility = facilities?.find(
    (facility) => facility?.id === facilityId
  );
  const officeHourId = rootFacility?.officeHourId;

  return facilities && facilities?.length > 0 ? (
    <Card>
      <Title level={5}>
        {formatMessage({ id: "facility-selection.list-title" })}
      </Title>
      {!officeHourId && (
        <p className={"facilityListSubHeadline"}>
          {formatMessage({
            id: facilityId
              ? "facility-selection.list-description"
              : "facility-selection.list-description.clinic",
          })}
        </p>
      )}
      <FacilitySearch
        size={"large"}
        placeholder={formatMessage({ id: "placeholder.search" })}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />
      <Divider />
      <AveliosSpinner spinning={loading}>
        {filteredFacilities && filteredFacilities!.length > 0 ? (
          <List
            itemLayout="horizontal"
            dataSource={filteredFacilities?.map((facility) => facility!)}
            renderItem={(item) => {
              const title = highlightTitle(item!.name!, searchQuery);
              const translatedDescription = extractTranslation(
                item.shortDescription,
                locale
              );

              const disabled =
                !item.clinicId ||
                currentUser?.accountVerificationRequiredForClinicIds?.includes(
                  item.clinicId
                );

              return (
                <List.Item
                  key={item.id}
                  style={
                    !disabled
                      ? { cursor: "pointer" }
                      : { cursor: "not-allowed", backgroundColor: "#f6f6f6" }
                  }
                  onClick={() => {
                    if (!disabled) {
                      setSearchQuery("");
                      router.push({
                        pathname: "facility-selection",
                        query: { facilityId: item?.id },
                      });
                    }
                  }}
                  actions={[<RightOutlined key={"right"} />]}
                >
                  <List.Item.Meta
                    className={styles.facilityListItemMeta}
                    avatar={
                      item.logoImageId ? (
                        <Avatar
                          className={styles.facilityLogo}
                          src={buildImageDownloadUrl(item.logoImageId)}
                          size={50}
                        />
                      ) : (
                        <Avatar size={50} />
                      )
                    }
                    title={title}
                    description={
                      <div>
                        <div
                          className={`${styles.clinicDescription} ant-list-item-meta-description description`}
                        >
                          {translatedDescription
                            ? highlightTitle(translatedDescription, searchQuery)
                            : formatMessage({
                                id: "facility-selection.no-description",
                              })}
                        </div>
                        {disabled && (
                          <div style={{ color: "darkred", marginTop: 20 }}>
                            {formatMessage({
                              id: "facility-selection.list.facility-disabled",
                            })}
                          </div>
                        )}
                      </div>
                    }
                  />
                </List.Item>
              );
            }}
          />
        ) : (
          <Empty
            description={formatMessage({ id: "facility-selection.no-data" })}
          />
        )}
      </AveliosSpinner>
    </Card>
  ) : null;
};
